import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Play, Plus, Trash2, Trophy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const Leaderboard = ({ rows }) => (
  <ol className="mt-3 space-y-1.5">
    {rows.slice(0, 8).map((s, i) => (
      <li key={s.user_id} data-testid={`leader-row-${i}`} className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm ${i === 0 ? "bg-gold-400/15 border border-gold-400/50" : "bg-surface-deep/50 border border-slate-200"}`}>
        <span className={`grid place-items-center w-7 h-7 rounded-full text-xs font-bold ${i === 0 ? "bg-gold-400 text-black" : i === 1 ? "bg-slate-600 text-black" : i === 2 ? "bg-brand text-white" : "bg-slate-100 text-slate-700"}`}>{i === 0 ? <Crown className="w-3.5 h-3.5" /> : i + 1}</span>
        <span className="flex-1 font-semibold text-white">{s.name}</span>
        <span className="font-mono text-xs text-slate-500">{s.attempts} tr{s.attempts === 1 ? "y" : "ies"}</span>
        <span className="font-mono font-bold text-gold-300">{s.best_pct}%</span>
      </li>
    ))}
    {!rows.length && <li className="text-sm text-slate-500">No scores yet. Be the first to take the quiz.</li>}
  </ol>
);

export const GroupRevise = ({ gid, me, isOwner }) => {
  const [packs, setPacks] = useState(null);
  const [mine, setMine] = useState([]);
  const [pick, setPick] = useState("");
  const load = () => api.get(`/groups/${gid}/packs`).then((r) => setPacks(Array.isArray(r.data) ? r.data : [])).catch(() => setPacks([]));
  useEffect(() => {
    load();
    api.get("/revision/packs").then((r) => setMine((Array.isArray(r.data) ? r.data : []).filter((p) => p.status === "done"))).catch(() => {});
  }, [gid]); // eslint-disable-line react-hooks/exhaustive-deps
  const share = async () => {
    try { await api.post(`/groups/${gid}/packs`, { pack_id: pick }); setPick(""); toast.success("Pack shared with the group"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (pid) => { if (!window.confirm("Remove this pack and its leaderboard from the group?")) return; await api.delete(`/groups/${gid}/packs/${pid}`).catch(() => {}); load(); };
  const shared = new Set((packs ?? []).map((p) => p.pack_id));
  const options = mine.filter((p) => !shared.has(p.pack_id));
  return (
    <section data-testid="group-revise" className="space-y-4">
      <div className="card p-4 sm:p-5">
        <p className="eyebrow">Revise together</p>
        <p className="mt-1 text-sm text-slate-600">Share one of your revision packs. Everyone can quiz on it and the best scores go on the leaderboard.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <select data-testid="group-pack-select" value={pick} onChange={(e) => setPick(e.target.value)} className="flex-1 min-w-[12rem] h-11 rounded-2xl border border-slate-200 bg-surface-deep/60 px-3 text-sm text-white outline-none">
            <option value="">{options.length ? "Pick one of your packs…" : "Make a pack on the Revise tab first"}</option>
            {options.map((p) => <option key={p.pack_id} value={p.pack_id}>{p.title}</option>)}
          </select>
          <button data-testid="group-pack-share" onClick={share} disabled={!pick} className="inline-flex items-center gap-2 h-11 rounded-2xl bg-gold-400 px-5 text-sm font-bold text-black hover:bg-gold-300 disabled:opacity-40"><Plus className="w-4 h-4" />Share pack</button>
        </div>
      </div>
      {packs === null && <div className="h-32 rounded-3xl bg-surface animate-pulse" />}
      {packs?.length === 0 && <p className="rounded-3xl border-2 border-dashed border-slate-200 p-8 text-center text-slate-500">No shared packs yet.</p>}
      <ul className="grid gap-3 lg:grid-cols-2">
        {(packs ?? []).map((p) => (
          <li key={p.pack_id} data-testid={`group-pack-${p.pack_id}`} className="card p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <span className="grid place-items-center w-10 h-10 shrink-0 rounded-xl bg-gold-400/15 text-gold-300"><Trophy className="w-5 h-5" /></span>
              <div className="flex-1 min-w-0"><h3 className="font-semibold text-white">{p.title}</h3><p className="text-xs text-slate-500">{p.questions} questions · shared by {p.by}</p></div>
              {(p.user_id === me || isOwner) && <button data-testid={`group-pack-remove-${p.pack_id}`} onClick={() => remove(p.pack_id)} aria-label="Remove pack" className="grid place-items-center w-8 h-8 rounded-lg text-slate-500 hover:bg-rose-600 hover:text-white"><Trash2 className="w-4 h-4" /></button>}
            </div>
            <Leaderboard rows={Array.isArray(p.leaderboard) ? p.leaderboard : []} />
            <Link to={`/quiz/${p.pack_id}?group=${gid}`} data-testid={`group-pack-quiz-${p.pack_id}`} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-brand px-4 py-3 text-sm font-bold text-white hover:bg-brand-dark shadow-red"><Play className="w-4 h-4" />Take the quiz</Link>
          </li>
        ))}
      </ul>
    </section>
  );
};
